/**
 * MarketingFeaturesPage.tsx — Admin page for managing marketing features
 *
 * Marketing features are display items shown on pricing pages.
 * They are NOT permissions/entitlements — purely for pricing page presentation.
 *
 * @since 2026-06-20
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';

interface MarketingFeature {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  category: string | null;
  displayOrder: number;
  isActive: boolean;
}

export default function MarketingFeaturesPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const apiBase = `${apiBaseUrl}/api/v1/orgs/${activeOrg?.orgId}`;
  const queryClient = useQueryClient();
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<MarketingFeature | null>(null);
  const [formData, setFormData] = useState({
    name: '', description: '', icon: '', category: '', displayOrder: 0,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['marketing-features'],
    queryFn: async () => {
      const res = await fetch(`${apiBase}/marketing-features`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load features');
      return res.json() as Promise<{ features: MarketingFeature[] }>;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (input: typeof formData) => {
      const res = await fetch(`${apiBase}/marketing-features`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to create feature');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-features'] });
      closePanel();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...input }: { id: string } & Partial<typeof formData>) => {
      const res = await fetch(`${apiBase}/marketing-features/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to update feature');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-features'] });
      closePanel();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${apiBase}/marketing-features/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to delete feature');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-features'] }),
  });

  function closePanel() {
    setPanelOpen(false);
    setEditingFeature(null);
    setFormData({ name: '', description: '', icon: '', category: '', displayOrder: 0 });
  }

  function openCreate() {
    setEditingFeature(null);
    setFormData({ name: '', description: '', icon: '', category: '', displayOrder: 0 });
    setPanelOpen(true);
  }

  function openEdit(f: MarketingFeature) {
    setEditingFeature(f);
    setFormData({
      name: f.name,
      description: f.description ?? '',
      icon: f.icon ?? '',
      category: f.category ?? '',
      displayOrder: f.displayOrder,
    });
    setPanelOpen(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editingFeature) {
      updateMutation.mutate({ id: editingFeature.id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  }

  const features = data?.features ?? [];
  const grouped = features.reduce<Record<string, MarketingFeature[]>>((acc, f) => {
    const cat = f.category || 'Uncategorized';
    (acc[cat] ??= []).push(f);
    return acc;
  }, {});

  return (
    <AdminLayout title="Marketing Features">
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Marketing Features</h1>
            <p style={{ color: '#64748B', fontSize: '0.9rem', marginTop: 4 }}>
              Curate the features shown on your pricing pages. These are for display only — not tied to permissions.
            </p>
          </div>
          <button
            onClick={openCreate}
            style={{ background: '#0F766E', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}
          >
            + Add Feature
          </button>
        </div>

        {isLoading ? (
          <p style={{ color: '#94A3B8' }}>Loading features…</p>
        ) : features.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, background: '#F8FAFC', borderRadius: 12, border: '1px dashed #CBD5E1' }}>
            <p style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 8 }}>No marketing features yet</p>
            <p style={{ color: '#64748B' }}>Create features to show on your dynamic pricing pages.</p>
          </div>
        ) : (
          Object.entries(grouped).map(([category, items]) => (
            <div key={category} style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748B', marginBottom: 12 }}>
                {category} ({items.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map(f => (
                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {f.icon && <span style={{ fontSize: '1.2rem' }}>{f.icon}</span>}
                      <div>
                        <div style={{ fontWeight: 600 }}>{f.name}</div>
                        {f.description && <div style={{ fontSize: '0.8rem', color: '#64748B' }}>{f.description}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => openEdit(f)} style={{ background: 'none', border: '1px solid #CBD5E1', borderRadius: 6, padding: '4px 12px', fontSize: '0.8rem', cursor: 'pointer' }}>
                        Edit
                      </button>
                      <button
                        onClick={() => { if (confirm(`Delete "${f.name}"?`)) deleteMutation.mutate(f.id); }}
                        style={{ background: 'none', border: '1px solid #FCA5A5', borderRadius: 6, padding: '4px 12px', fontSize: '0.8rem', cursor: 'pointer', color: '#DC2626' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Side Panel (slides in from right) ── */}
      {panelOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={closePanel}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 999 }}
          />
          {/* Panel */}
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 440,
            background: '#fff', zIndex: 1000, display: 'flex', flexDirection: 'column',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
            animation: 'slideInRight 0.2s ease-out',
          }}>
            {/* Header */}
            <div style={{
              background: '#0F172A', color: '#fff', padding: '16px 20px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px', color: '#94A3B8', marginBottom: 4 }}>
                  MARKETING FEATURES
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                  {editingFeature ? 'Edit Feature' : 'New Feature'}
                </div>
                {editingFeature && (
                  <div style={{ fontSize: '0.8rem', color: '#94A3B8', marginTop: 2 }}>{editingFeature.name}</div>
                )}
              </div>
              <button onClick={closePanel} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer', padding: '4px 8px' }}>
                ×
              </button>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSubmit} style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 6, color: '#334155' }}>Name *</label>
                <input
                  value={formData.name}
                  onChange={e => setFormData(d => ({ ...d, name: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: '0.9rem' }}
                  placeholder="e.g. Unlimited Projects"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 6, color: '#334155' }}>Category</label>
                <input
                  value={formData.category}
                  onChange={e => setFormData(d => ({ ...d, category: e.target.value }))}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: '0.9rem' }}
                  placeholder="e.g. Core, Advanced, Support"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 6, color: '#334155' }}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(d => ({ ...d, description: e.target.value }))}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: '0.9rem', minHeight: 80, resize: 'vertical' }}
                  placeholder="Longer description for tooltips or expanded pricing views"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 6, color: '#334155' }}>Icon (emoji or name)</label>
                  <input
                    value={formData.icon}
                    onChange={e => setFormData(d => ({ ...d, icon: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: '0.9rem' }}
                    placeholder="e.g. 🚀 or rocket"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 6, color: '#334155' }}>Display Order</label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={e => setFormData(d => ({ ...d, displayOrder: parseInt(e.target.value) || 0 }))}
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: '0.9rem' }}
                  />
                </div>
              </div>

              {/* Spacer */}
              <div style={{ flex: 1 }} />

              {/* Footer Buttons */}
              <div style={{ display: 'flex', gap: 12, borderTop: '1px solid #E2E8F0', paddingTop: 16 }}>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  style={{ flex: 1, background: '#0F766E', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 20px', fontWeight: 600, cursor: 'pointer', opacity: (createMutation.isPending || updateMutation.isPending) ? 0.6 : 1 }}
                >
                  {(createMutation.isPending || updateMutation.isPending)
                    ? 'Saving...'
                    : editingFeature ? 'Save Changes' : 'Create Feature'}
                </button>
                <button type="button" onClick={closePanel} style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1', borderRadius: 8, padding: '12px 20px', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
          <style>{`
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
          `}</style>
        </>
      )}
    </AdminLayout>
  );
}
